import {
	ArchiveRestore,
	CalendarPlus,
	Info,
	Loader2,
	MessageCircleMore,
	MoreHorizontal,
	Pencil,
	Stethoscope,
	UserRoundX,
	X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
	AppUser,
	isManagementEnded,
	PatientStatus,
	RiskLevel,
	type PatientListItem,
} from "@/api/adapters/patients";
import { modal } from '@/app/store/modal.store';
import { useInterventionPanelStore } from "@/app/store/intervention-panel.store";
import { useAuthCanWriteIntervention } from "@/app/store/auth.store";
import {
	ageFromBirth,
	currentProWeekIndex,
	formatDate,
	formatPhone,
	genderAbbr,
	maskMrn,
	maskNameKeepLast,
	maskPhone,
	treatmentWeeks,
} from "@/pages/medical/screens/home/utils";
import {
	useClosePatient,
	useEditPatient,
	useRevertPatient,
} from '@/shared/hooks/use-patient-actions';
import {
	formatBoundary,
	resolveRiskBands,
} from '@/features/patients/r-index-bands';
import type { PatientRegistrationFormValues } from '@/features/patients/schemas/patient-registration.schema';
import type { PatientEditInput } from '@/api/adapters/patients';
import { PatientFormModal } from './PatientFormModal';
import { ClosePatientModal } from './ClosePatientModal';
import type { ClosePatientFormValues } from '@/features/patients/schemas/close-patient.schema';

/**
 * SHCT-675 — the popover lists the bands in the order they are configured, so the badge colour and the
 * copy key are looked up from the band CODE rather than written out three times in the markup. Colours
 * are PRD 11 §7.2: 저위험 초록 · 중위험 노랑 · 고위험 빨강.
 */
const R_BADGE_MOD = { LOW: 'low', MODERATE: 'mod', HIGH: 'high' } as const;
const R_LEVEL_KEY = { LOW: 'low', MODERATE: 'moderate', HIGH: 'high' } as const;

interface Props {
	patient: PatientListItem;
	/**
	 * The patient's own mobile, UNMASKED, from the detail endpoint (PRD 04 v2.30 「표시 정보」). Passed in
	 * rather than read off `patient` because `patient` is usually the patient-list row, and the list
	 * response masks the number (PRD 03). Undefined while the detail request is in flight.
	 */
	phone?: string | null;
	onScheduleAdd?: () => void;
}

export function PatientDetailHeader({ patient, phone, onScheduleAdd }: Props) {
	const { t } = useTranslation(["medical", "common"]);
	const [showPopover, setShowPopover] = useState(false);
	const [showMore, setShowMore] = useState(false);
	const [showCloseModal, setShowCloseModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const popoverRef = useRef<HTMLDivElement>(null);
	const rInfoBtnRef = useRef<HTMLButtonElement>(null);
	const moreMenuRef = useRef<HTMLDivElement>(null);
	const moreBtnRef = useRef<HTMLButtonElement>(null);

	const isPatientActive = patient.status === PatientStatus.ACTIVE;
	const isPatientClosed = patient.status === PatientStatus.CLOSED;
	// SHCT-762 — 탈퇴(WITHDRAWN) 환자는 CLOSED 와 동일하게 "종료" 취급한다. 액션 잠금·기간
	// 배지·R칩은 모두 이 값을 봐야 하고, CLOSED 만 보면 탈퇴 환자가 활성 환자처럼 편집·개입
	// 생성이 열린 채로 남는다. 단 [관리종료]·[관리종료 취소] 두 액션만은 예외로 아예 감춘다
	// (티켓: "except End and Cancel End Management is not available") — 탈퇴는 임상 종료가
	// 아니라 계정 이탈이라 되돌릴 대상이 없기 때문.
	const isPatientWithdrawn = patient.status === PatientStatus.WITHDRAWN;
	const isTerminated = isManagementEnded(patient.status);

	const closePatientMutation = useClosePatient();
	const revertPatientMutation = useRevertPatient();
	const editPatientMutation = useEditPatient();
	const isMutating = closePatientMutation.isPending || revertPatientMutation.isPending;

	// Close R-index popover on outside click
	useEffect(() => {
		if (!showPopover) return;
		const onClick = (e: MouseEvent) => {
			if (
				popoverRef.current &&
				!popoverRef.current.contains(e.target as Node) &&
				rInfoBtnRef.current &&
				!rInfoBtnRef.current.contains(e.target as Node)
			) {
				setShowPopover(false);
			}
		};
		document.addEventListener("click", onClick);
		return () => document.removeEventListener("click", onClick);
	}, [showPopover]);

	// Close more menu on outside click
	useEffect(() => {
		if (!showMore) return;
		const onClick = (e: MouseEvent) => {
			if (
				moreMenuRef.current &&
				!moreMenuRef.current.contains(e.target as Node) &&
				moreBtnRef.current &&
				!moreBtnRef.current.contains(e.target as Node)
			) {
				setShowMore(false);
			}
		};
		document.addEventListener("click", onClick);
		return () => document.removeEventListener("click", onClick);
	}, [showMore]);

	const handleManageClick = () => {
		if (isPatientClosed) {
			setShowMore(false);
			modal.confirm(
				t('medical:patients.header.actions.revertConfirmTitle'),
				t('medical:patients.header.actions.revertConfirmBody', {
					name: maskNameKeepLast(patient.name),
				}),
				() => revertPatientMutation.mutate(patient.uid),
			);
		} else if (isPatientActive) {
			setShowCloseModal(true);
			setShowMore(false);
		}
	};

	const handleCloseSubmit = (values: ClosePatientFormValues) => {
		closePatientMutation.mutate(
			{
				uid: patient.uid,
				reason: values.reason,
				endDate: values.endDate,
				memo: values.memo,
			},
			{ onSuccess: () => setShowCloseModal(false) },
		);
	};

	const handleEditSubmit = (values: PatientRegistrationFormValues) => {
		// Strip appUser only; mrn is sent because the BE currently requires it (TBD_BE_MRN_REQUIRED).
		const { appUser: _a, ...editData } = values;
		editPatientMutation.mutate(
			{ uid: patient.uid, data: editData as PatientEditInput },
			{
				onSuccess: (result) => {
					setShowEditModal(false);
					// PRD §3.3: 퇴원일(PRO 기준일) 변경 시 PRO 스케줄이 재계산됨을 확인 모달로 안내.
					if (result.proRecalcRequired) {
						modal.alert(
							t('medical:patients.edit.proRecalc.title'),
							t('medical:patients.edit.proRecalc.body'),
						);
					}
				},
			},
		);
	};

	// v1.1 §3.1 조건 분기: 관리종료(status CLOSED) → 종료 사유 chip + 편집 액션 비활성
	const isClosed = isPatientClosed;
	// Both treatment badges are Monday-anchored (matching the PRO calendar's highlighted column), and the
	// ended one is FROZEN at 종료일 — see treatmentWeeks. WITHDRAWN counts as ended alongside CLOSED: the BA's
	// rule names close AND withdraw, and although today's BE routes a self-withdrawal to CLOSED +
	// closedReason=WITHDRAWN, `MedicalPatientService.DB_STATUSES` still admits a bare WITHDRAWN row.
	const hasEnded = isTerminated;
	const badgeWeeks = treatmentWeeks(
		patient.dischargeDate,
		hasEnded,
		patient.closeDate,
	);
	// PD-07: CRC cannot create interventions/schedules → hide the write actions.
	const canWrite = useAuthCanWriteIntervention();
	// SHCT-762 — 종료 축은 CLOSED 가 아니라 isTerminated. 탈퇴 환자도 편집·개입 생성이 잠긴다.
	const actionsDisabled = isTerminated || !canWrite;
	const riskLevel = patient.riskLevel ?? "";
	// PD-01 v2.5 §3.1 / PRD 11 §7.2: R-chip has three states —
	//  • 산출 불가 = active AND riskLevel 'UNKNOWN' (BE RiskComputation Rule B — submitted but unscoreable) → red chip
	//  • a real riskScore → graded R pill (저/중/고)
	//  • otherwise (management-ended, OR untouched/never-submitted → riskLevel null) → gray "R —"
	// Key off riskLevel, NOT riskScore==null: an untouched new patient also has a null score but must NOT be
	// forced onto the red 산출 불가 chip (BE deliberately leaves such patients at riskLevel null, not 'UNKNOWN').
	// Gate on ACTIVE (not merely !CLOSED): a WITHDRAWN(탈퇴) patient is also terminal, so it must show gray
	// "R —" like a closed one — never the active-only red 산출 불가 chip (was `!isClosed`, which let
	// withdrawn+UNKNOWN patients render red next to closed+UNKNOWN patients rendering gray).
	// WITHDRAWN is a distinct top-level status (not a CLOSED sub-reason; BE MedicalPatientService DB_STATUSES),
	// so a terminated patient with a real score must ALSO be suppressed to gray "R —" — otherwise a
	// withdrawn+score patient renders a colored pill (rHasScore) next to closed patients' gray "R —".
	// The set is named once, in the adapter beside the status codes themselves, so a status added later
	// forces a decision there rather than silently changing every screen that spelled the set out locally.
	// (`isTerminated` is declared once at the top of the component — SHCT-762 hoisted it so the
	//  action-lock and period-badge axes read the same value the R-chip already did.)
	// SHCT-675 — the band boundaries the ⓘ popover prints, from the admin configuration the server sends.
	// Falls back to the V70 seed values (and says so) while medical-api has no boundaries on the response.
	const rBands = resolveRiskBands(patient.riskBands);
	const rCannotCompute =
		isPatientActive && patient.riskLevel === RiskLevel.UNKNOWN;
	const rHasScore =
		!rCannotCompute && !isTerminated && patient.riskScore != null;
	const rPillMod =
		riskLevel === RiskLevel.G3
			? "high"
			: riskLevel === RiskLevel.G2
				? "mod"
				: riskLevel === RiskLevel.G1
					? "low"
					: "empty";
	const riskLabel =
		riskLevel === RiskLevel.G3
			? t("medical:patients.header.riskLevel.high")
			: riskLevel === RiskLevel.G2
				? t("medical:patients.header.riskLevel.moderate")
				: riskLevel === RiskLevel.G1
					? t("medical:patients.header.riskLevel.low")
					: "";

	// Treatment period end (만기일) = 퇴원일 + 26 weeks — the admin-defined 26-week treatment span,
	// matching the 26-column PRO calendar (ProCalendar TOTAL_WEEKS). Was `+6 calendar months`, which is a
	// different length (181–184 days) and did not match the 26-week definition.
	// Date-only math on the LOCAL date: must NOT round-trip through toISOString()/UTC — that rolls the date
	// back a day in UTC+ timezones (KST +9 / Sydney +10), which showed 만기일 one day early on screen.
	const endDate = new Date(patient.dischargeDate + "T00:00:00");
	endDate.setDate(endDate.getDate() + 26 * 7);
	const endStr = formatDate(
		`${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, "0")}-${String(
			endDate.getDate(),
		).padStart(2, "0")}`,
	);

	return (
		<header className="ph">
			<div className="ph__top">
				{/* ── Identity ── */}
				<div className="ph__identity">
					<h2 className="ph__name">{maskNameKeepLast(patient.name)}</h2>
					<div className="ph__info-stack">
						<div className="ph__meta-row">
							{/* v1.1: 생년월일 (성별약자+나이) · MRN 뱃지 */}
							<span className="ph__meta-text">
								{formatDate(patient.birthDate)}
							</span>
							{genderAbbr(patient.gender) && (
								<span className="ph__meta-text">
									({genderAbbr(patient.gender)}
									{/* Age only when a birth date exists — ageFromBirth('') returns 0,
									    which rendered a bogus "(M0)" for patients whose DOB is not yet
									    delivered (patient-mode, pre-PASS). */}
									{patient.birthDate ? ageFromBirth(patient.birthDate) : ""})
								</span>
							)}
							<span className="ph__sep" />
							{/* PRD §2.1/§3.1 top row: 생년월일 (성별+나이) · [MRN 뱃지] only — no UID field. */}
							<span className="ph__id">
								<span className="ph__id-label">MRN</span>
								<span className="ph__id-val">{maskMrn(patient.mrn)}</span>
							</span>
							{/* PRD 04 v2.30 표시 정보 — 환자 본인 휴대폰을 MRN 뱃지 오른쪽에 마스킹 없이 표시한다.
							    전화 중재(§3.4)가 같은 화면에 있어 마스킹된 번호로는 발신할 수 없기 때문이다.
							    구분선이 반드시 앞에 온다: `.ph__meta-row` 는 `gap: 0` 이라 이 줄의 간격은 전부
							    `ph__sep` 가 만든다. 없이 두면 MRN 값과 번호가 붙어 한 덩어리로 읽힌다 —
							    §2.1 레이아웃 도해도 `[MRN 20260049] · 010-1234-5678` 로 구분해 그린다. */}
							{formatPhone(phone) && (
								<>
									<span className="ph__sep" />
									<span className="ph__meta-text">{formatPhone(phone)}</span>
								</>
							)}
						</div>
						<div className="ph__tags-row">
							{/* PRD 04 §3.1 아랫줄: 주소 · 주진단 · 부진단 · 치료기간 · 담당 의료진 */}
							{patient.address && (
								<>
									<span className="ph__meta-text">{patient.address}</span>
									<span className="ph__sep" />
								</>
							)}
							<span
								className="ph__dx-badge ph__dx-badge--primary"
								title={t("medical:patients.header.diagnosis.primary")}
							>
								{patient.primaryDxName}
							</span>
							{patient.secondaryDx && (
								<span
									className="ph__dx-badge ph__dx-badge--sub"
									title={t("medical:patients.header.diagnosis.secondary")}
								>
									{patient.secondaryDx}
									{patient.secondaryDxName
										? ` · ${patient.secondaryDxName}`
										: ""}
								</span>
							)}
							<span className="ph__sep" />
							<span className="ph__period-label">
								{t("medical:patients.header.treatment.period")}
							</span>
							<span className="ph__period-range">
								{formatDate(patient.dischargeDate)} ~ {endStr}
							</span>
							{hasEnded ? (
								<span className="ph__period-badge ph__period-badge--closed">
									{/* Header closed label = "{weeks}주 후 종료 · {사유}" (prototype periodElapsedLabel);
									    closedReason is a BE code (COMPLETED/…) → short 만기/탈퇴/재입원/기타.
									    No 종료일 from the server → drop the week number, keep the 사유. */}
									{badgeWeeks == null
										? t("medical:patients.header.treatment.ended")
										: t("medical:patients.header.treatment.weeksToEnd", {
												weeks: badgeWeeks,
											})}
									{/* SHCT-762 — a bare WITHDRAWN row carries no closedReason (the reason IS the
									    status), so fall back to the WITHDRAWN label rather than dropping the
									    사유 and rendering an unqualified "종료". */}
									{(() => {
										const reason =
											patient.closedReason ??
											(isPatientWithdrawn ? "WITHDRAWN" : null);
										return reason
											? ` · ${t(
													`medical:patients.header.treatment.closeReasonShort.${reason}`,
													{ defaultValue: reason },
												)}`
											: "";
									})()}
								</span>
							) : (
								<span className="ph__period-badge">
									{t("medical:patients.card.weeksElapsed", {
										weeks: badgeWeeks ?? 0,
									})}
								</span>
							)}
							{/* MED.008 · CR US-PD-CR01: 담당 의료진(주치의) 뱃지 — 미배정 시 '담당 미배정' */}
							<span className="ph__sep" />
							<span
								className="ph__staff-badge"
								title={t("medical:patients.header.diagnosis.staff", {
									defaultValue: "담당 의료진",
								})}
							>
								<Stethoscope size={14} />
								{patient.primaryStaff
									? t("medical:patients.header.staff.inCharge", {
											name: patient.primaryStaff,
										})
									: t("medical:patients.header.staff.unassigned")}
							</span>
						</div>
						{/* SHCT-915 — 상세 주소 는 윗줄(주소·진단·치료기간) 바로 아래 자기 줄에 둔다 (고객 확정
						    2026.09.10). 주소 옆에 붙이지 않는 이유는 이 값이 환자가 직접 입력하는 자유 텍스트
						    (최대 100자 — `[공통] 00_입력검증표준` §5-⑦) 이기 때문이다. 한 줄에 같이 두면
						    `.ph__meta-text` 의 `nowrap` 때문에 줄이 늘어나 R칩과 우측 액션 버튼이 화면 밖으로
						    밀렸다(QA 관측). 자기 줄에서는 줄바꿈으로 흡수된다.
						    값은 라벨 없이 그대로 출력하고, 비워 둔 환자는 어드민 패널과 같이 `-` 로 표시한다.
						    응답에 필드 자체가 없는 동안(아직 내려주지 않는 서버)에는 줄을 그리지 않는다 —
						    데이터가 도착하지 않았을 뿐인데 전 환자에게 `-` 한 줄을 보여 줄 이유가 없다. */}
						{patient.residenceDetail !== undefined && (
							<div className="ph__detail-address">
								<span className="ph__meta-text ph__meta-text--wrap">
									{patient.residenceDetail?.trim() || "-"}
								</span>
							</div>
						)}
						{/* PRD 04 §3.1: 보호자 대리 앱 라인 — 별도 줄 (이름 마스킹 · 관계 · 휴대폰 마스킹) */}
						{patient.appUser === AppUser.GUARDIAN && patient.guardian && (
							<div className="ph__guardian-row">
								<span className="ph__guardian-pill">
									<span className="ph__guardian-dot">
										{t("medical:patients.card.guardianShort")}
									</span>
									<span className="ph__guardian-text">
										{maskNameKeepLast(patient.guardian.name)} (
										{t(
											`medical:patients.register.staff.relationship.${patient.guardian.relationship}`,
										)}
										)
										{patient.guardian.mobile
											? ` · ${maskPhone(patient.guardian.mobile)}`
											: ""}
									</span>
								</span>
							</div>
						)}
					</div>
				</div>

				<div className="ph__divider" />

				{/* ── R-pill ── */}
				<span
					className="ph__r-wrap relative inline-flex items-center"
				>
					{rCannotCompute ? (
						<span
							className="ph__r-pill ph__r-pill--high"
							title={t("medical:patients.header.rIndex.cannotCompute")}
						>
							{t("medical:patients.header.rIndex.cannotCompute")}
						</span>
					) : rHasScore ? (
						<span
							className={`ph__r-pill ph__r-pill--${rPillMod}`}
							title={t("medical:patients.header.rIndex.rPillTooltip", {
								level: riskLabel,
							})}
						>
							R {patient.riskScore?.toFixed(1)} · {riskLabel}
						</span>
					) : (
						<span className="ph__r-pill ph__r-pill--empty">R —</span>
					)}
					<button
						ref={rInfoBtnRef}
						type="button"
						className="ph__r-info"
						aria-label={t("medical:patients.header.rIndex.infoAria")}
						title={t("medical:patients.header.rIndex.infoAria")}
						onClick={() => setShowPopover((v) => !v)}
					>
						<Info size={16} />
					</button>
					{showPopover && (
						<div
							ref={popoverRef}
							className="r-popover absolute top-full mt-2 right-0"
							role="dialog"
							aria-label={t("medical:patients.header.rIndex.label")}
						>
							<button
								type="button"
								className="r-popover__close"
								onClick={() => setShowPopover(false)}
								aria-label={t("common:actions.close")}
							>
								<X size={16} />
							</button>
							<p className="r-popover__title">
								<Info size={16} className="w-4 h-4" />{" "}
								{t("medical:patients.header.rIndex.label")}
							</p>
							<div>
								<Trans
									i18nKey="medical:patients.header.rIndex.description"
									components={{ strong: <strong /> }}
								/>
							</div>
							{/* SHCT-675 — the boundaries are printed from the admin configuration the server
							    sends, never from the copy: 「경계값은 어드민에서 관리」(PRD 04 §3.1 · 11 §7.2). */}
							<div className="r-popover__levels">
								{rBands.bands.map((b) => (
									<span
										key={b.band}
										className={`r-badge r-badge--${R_BADGE_MOD[b.band]}`}
									>
										{t(
											`medical:patients.header.rIndex.levels.${R_LEVEL_KEY[b.band]}`,
											{
												min: formatBoundary(b.min),
												max: formatBoundary(b.max),
											},
										)}
									</span>
								))}
							</div>
							<ul>
								{/* SHCT-934 — the scale has no top (PRD 04 v2.38 §3.1 · [ADM.024]): the configured
								    high.max is not a cap, so only the bottom of the lowest band is printed. */}
								<li>
									{t("medical:patients.header.rIndex.range", {
										min: formatBoundary(rBands.min),
									})}
								</li>
								{/* SHCT-934 — PRD 04 §3.1 lists only 의미·범위·등급·산출·산출 불가 and forbids any other
								    calculation copy (문항 수 · 산출 대상 지표), so the old "23개 핵심 문항 + 생체지표" line is gone
								    and 산출 불가 sits in this list, as the prototype draws it. */}
								<li>{t("medical:patients.header.rIndex.cannotComputeNote")}</li>
							</ul>
							<div className="r-popover__note">
								{t("medical:patients.header.rIndex.note")}
							</div>
							{/* Says so out loud rather than passing the shipped defaults off as the hospital's
							    own configuration — the numbers above are only right until someone edits them. */}
							{rBands.isSeed && (
								<div className="r-popover__note">
									{t("medical:patients.header.rIndex.seedNote")}
								</div>
							)}
						</div>
					)}
				</span>

				{/* ── Action buttons ── */}
				<div className="ph__right">
					<button
						type="button"
						className="ph__sch-btn btn"
						title={t("medical:patients.header.actions.addSchedule")}
						onClick={onScheduleAdd}
						disabled={actionsDisabled}
					>
						<CalendarPlus size={20} />{" "}
						{t("medical:patients.header.actions.addSchedule")}
					</button>
					<button
						type="button"
						className="ph__int-btn btn"
						title={t("medical:patients.header.actions.newIntervention")}
						onClick={() =>
							useInterventionPanelStore.getState().openPanel(patient)
						}
						disabled={actionsDisabled}
					>
						<MessageCircleMore size={20} />{" "}
						{t("medical:patients.header.actions.newIntervention")}
					</button>
					<div className="ph__more-wrap">
						<button
							ref={moreBtnRef}
							type="button"
							className="ph__more-btn btn"
							onClick={() => setShowMore((v) => !v)}
							aria-label={t("medical:patients.header.actions.more")}
							title={t("medical:patients.header.actions.more")}
							disabled={!canWrite}
						>
							<MoreHorizontal size={20} />
						</button>
						{showMore && (
							<div
							ref={moreMenuRef}
							className="ph__more-menu absolute top-full mt-1 right-0 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-20 min-w-40"
							data-visible="true"
							>
							<button
								type="button"
								disabled={actionsDisabled}
								onClick={() => {
									setShowMore(false);
									setShowEditModal(true);
								}}
								className="ph__more-item flex items-center gap-2 w-full px-3.5 py-2 border-0 bg-transparent cursor-pointer text-sm btn"
							>
								<Pencil size={16} />
								{t('medical:patients.header.actions.editPatient')}
							</button>
							{/* SHCT-762 — 탈퇴 환자에게는 [관리종료]·[관리종료 취소] 를 노출하지 않는다.
							    두 액션 모두 CLOSED 축에서만 의미가 있고, handleManageClick 은 WITHDRAWN
							    에서 어느 분기도 타지 않아 그대로 두면 눌리지만 아무 일도 없는 버튼이 된다. */}
							{!isPatientWithdrawn && (
								<button
									type="button"
									disabled={isMutating}
									onClick={handleManageClick}
									className={`ph__more-item flex items-center gap-2 w-full px-3.5 py-2 border-0 bg-transparent cursor-pointer text-sm ${
										isClosed ? "" : "ph__more-item--danger text-red-500"
									}`}
								>
									{isMutating ? (
										<Loader2 size={16} className='animate-spin' />
									) : isClosed ? (
										<ArchiveRestore size={16} />
									) : (
										<UserRoundX size={16} />
									)}
									{isClosed
										? t('medical:patients.header.actions.cancelCloseManagement')
										: t('medical:patients.header.actions.closeManagement')}
								</button>
							)}
							</div>
						)}
					</div>
				</div>
			</div>
			<ClosePatientModal
				open={showCloseModal}
				patient={patient}
				isSubmitting={closePatientMutation.isPending}
				onClose={() => setShowCloseModal(false)}
				onSubmit={handleCloseSubmit}
			/>
			<PatientFormModal
				open={showEditModal}
				mode="edit"
				patient={patient}
				onClose={() => setShowEditModal(false)}
				onSubmit={handleEditSubmit}
				isSubmitting={editPatientMutation.isPending}
			/>
		</header>
	);
}
